/**
 * Print/Export Utility Functions
 *
 * Computes print bounds, multi-page tiling, magnetic declination,
 * and scale formatting for the MGRS Topographic Map print system.
 *
 * Pure JS — no React, no DOM dependencies.
 */

/** Meters per inch at Earth's surface (used for scale calculations) */
const METERS_PER_INCH = 0.0254

/**
 * Paper dimensions in inches (printable area after margins).
 * Margins: 0.5" each side for Letter, 12.7mm (~0.5") each side for A4.
 */
const PAPER_SIZES = {
  letter: {
    widthIn: 8.5 - 1.0,   // 7.5" printable
    heightIn: 11.0 - 1.0,  // 10.0" printable
  },
  a4: {
    widthIn: (210 - 25.4) / 25.4,  // ~7.27" printable
    heightIn: (297 - 25.4) / 25.4, // ~10.69" printable
  },
}

/**
 * Approximate meters per degree of latitude (roughly constant).
 */
const METERS_PER_DEG_LAT = 111320

/**
 * Approximate meters per degree of longitude at a given latitude.
 *
 * @param {number} lat - Latitude in decimal degrees
 * @returns {number} Meters per degree of longitude
 */
function metersPerDegLng(lat) {
  return METERS_PER_DEG_LAT * Math.cos((lat * Math.PI) / 180)
}

/**
 * Compute the geographic bounds that fit on one printed page at a given scale.
 *
 * @param {{ lat: number, lng: number }} center - Map center
 * @param {number} scale - Map scale denominator (e.g. 50000 for 1:50,000)
 * @param {'letter'|'a4'} paperSize - Paper size identifier
 * @returns {{
 *   north: number, south: number, east: number, west: number,
 *   widthKm: number, heightKm: number
 * }}
 */
export function computePrintBounds(center, scale, paperSize) {
  const paper = PAPER_SIZES[paperSize] || PAPER_SIZES.letter

  // Ground distance covered by the printable area
  const widthMeters = paper.widthIn * METERS_PER_INCH * scale
  const heightMeters = paper.heightIn * METERS_PER_INCH * scale

  // Convert ground distance to degrees
  const halfHeightDeg = (heightMeters / 2) / METERS_PER_DEG_LAT
  const halfWidthDeg = (widthMeters / 2) / metersPerDegLng(center.lat)

  const north = center.lat + halfHeightDeg
  const south = center.lat - halfHeightDeg
  const east = center.lng + halfWidthDeg
  const west = center.lng - halfWidthDeg

  return {
    north,
    south,
    east,
    west,
    widthKm: widthMeters / 1000,
    heightKm: heightMeters / 1000,
  }
}

/**
 * Divide a geographic area into page-sized tiles for multi-page printing.
 *
 * @param {{ north: number, south: number, east: number, west: number }} bounds
 * @param {number} scale - Map scale denominator
 * @param {'letter'|'a4'} paperSize - Paper size identifier
 * @returns {Array<{ row: number, col: number, bounds: object, pageLabel: string }>}
 */
export function computeTileGrid(bounds, scale, paperSize) {
  const paper = PAPER_SIZES[paperSize] || PAPER_SIZES.letter

  // Ground distance per page
  const pageWidthMeters = paper.widthIn * METERS_PER_INCH * scale
  const pageHeightMeters = paper.heightIn * METERS_PER_INCH * scale

  // Total geographic span
  const centerLat = (bounds.north + bounds.south) / 2
  const totalWidthDeg = bounds.east - bounds.west
  const totalHeightDeg = bounds.north - bounds.south

  // Degrees per page
  const pageWidthDeg = pageWidthMeters / metersPerDegLng(centerLat)
  const pageHeightDeg = pageHeightMeters / METERS_PER_DEG_LAT

  // Number of tiles needed
  const cols = Math.max(1, Math.ceil(totalWidthDeg / pageWidthDeg))
  const rows = Math.max(1, Math.ceil(totalHeightDeg / pageHeightDeg))

  const tiles = []

  // Index tile (overview)
  tiles.push({
    row: -1,
    col: -1,
    bounds: { ...bounds },
    pageLabel: 'Index',
  })

  // Detail tiles (top-left to bottom-right, row-major)
  for (let r = 0; r < rows; r++) {
    for (let c = 0; c < cols; c++) {
      const tileWest = bounds.west + c * pageWidthDeg
      const tileEast = Math.min(bounds.west + (c + 1) * pageWidthDeg, bounds.east)
      const tileNorth = bounds.north - r * pageHeightDeg
      const tileSouth = Math.max(bounds.north - (r + 1) * pageHeightDeg, bounds.south)

      tiles.push({
        row: r,
        col: c,
        bounds: {
          north: tileNorth,
          south: tileSouth,
          east: tileEast,
          west: tileWest,
        },
        pageLabel: `${String.fromCharCode(65 + r)}${c + 1}`,
      })
    }
  }

  return tiles
}

/**
 * Return approximate magnetic declination for NE Minnesota.
 *
 * Uses a simple linear interpolation model centered on the region:
 *   - At 47.5N, 92.5W: ~1.5 E
 *   - Decreases slightly east, increases slightly west
 *
 * @param {number} lat - Latitude in decimal degrees
 * @param {number} lng - Longitude in decimal degrees (negative for west)
 * @returns {{ declination: number, direction: 'E'|'W', year: number }}
 */
export function getMagneticDeclination(lat, lng) {
  // Reference point: 47.5N, -92.5 => 1.5 E
  const refLat = 47.5
  const refLng = -92.5
  const refDec = 1.5

  // Gradients (degrees declination per degree of geographic coordinate)
  // Declination increases ~0.3 per degree westward (more negative lng)
  // Declination changes ~0.1 per degree northward
  const dDecPerDegLng = -0.3  // going east (more positive lng) decreases declination
  const dDecPerDegLat = 0.1   // going north increases slightly

  const dLat = lat - refLat
  const dLng = lng - refLng

  let declination = refDec + dDecPerDegLat * dLat + dDecPerDegLng * dLng

  // Round to one decimal
  declination = Math.round(declination * 10) / 10

  const direction = declination >= 0 ? 'E' : 'W'

  return {
    declination: Math.abs(declination),
    direction,
    year: 2024,
  }
}

/**
 * Format a numeric scale as a display string.
 *
 * @param {number} scale - Scale denominator (e.g. 50000)
 * @returns {string} Formatted string (e.g. "1:50,000")
 */
export function formatScaleText(scale) {
  return `1:${scale.toLocaleString('en-US')}`
}
